/**
 * NewAttend 프랜차이즈 마케팅 홈페이지
 * - 도메인: newattend.com / www.newattend.com
 * - 용도: 가맹점주 모집, 본부 소개, 제품 안내
 * - 독서실 운영 시스템(app.newattend.com)과 별개
 */

import React from 'react';
import {
  AcademicCapIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const NewAttendFranchiseHomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-blue.png" alt="NewAttend 로고" className="h-10 w-10 rounded-lg" />
            <span className="text-2xl font-bold text-gray-900">NewAttend</span>
            <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">프랜차이즈</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-gray-700">
            <a href="#about" className="hover:text-blue-600">소개</a>
            <a href="#features" className="hover:text-blue-600">서비스</a>
            <a href="#franchise" className="hover:text-blue-600">가맹문의</a>
            <a href="#contact" className="hover:text-blue-600">문의</a>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href="https://app.newattend.com/owner/login"
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
            >
              가맹점주 로그인
            </a>
            <a
              href="https://hq.newattend.com/"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
            >
              본부 시스템
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight">
            AI 얼굴인식으로 운영하는<br />
            <span className="text-blue-600">스마트 독서실 프랜차이즈</span>
          </h1>
          <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
            NewAttend와 함께 시작하는 독서실 사업.<br />
            본부 통합 관리 시스템으로 안정적인 운영을 지원합니다.
          </p>
          <div className="mt-10 flex items-center justify-center gap-4">
            <a
              href="#franchise"
              className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold text-lg hover:bg-blue-700 shadow-lg"
            >
              가맹 문의하기
            </a>
            <a
              href="#features"
              className="px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold text-lg hover:bg-gray-50 border border-gray-200"
            >
              서비스 알아보기
            </a>
          </div>
        </div>
      </section>

      {/* 서비스 특징 */}
      <section id="features" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">왜 NewAttend인가?</h2>
          <p className="text-center text-gray-600 mb-16">검증된 시스템과 본부 지원으로 안전하게 시작하세요</p>
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<SparklesIcon className="h-8 w-8" />}
              title="AI 얼굴인식 출결"
              desc="태블릿 카메라로 자동 입퇴실 처리. 99.2% 정확도"
            />
            <FeatureCard
              icon={<ChartBarIcon className="h-8 w-8" />}
              title="실시간 학습 분석"
              desc="순공부시간, 출결률, 좌석 효율 통계 한눈에"
            />
            <FeatureCard
              icon={<ShieldCheckIcon className="h-8 w-8" />}
              title="본부 통합 관리"
              desc="전 가맹점 현황을 본부 대시보드에서 실시간 모니터링"
            />
            <FeatureCard
              icon={<UserGroupIcon className="h-8 w-8" />}
              title="학부모 알림톡"
              desc="입실/퇴실 카카오 알림톡으로 학부모 안심"
            />
            <FeatureCard
              icon={<AcademicCapIcon className="h-8 w-8" />}
              title="입시 컨설팅"
              desc="5년 누적 데이터 기반 학과 매칭 시스템"
            />
            <FeatureCard
              icon={<ShieldCheckIcon className="h-8 w-8" />}
              title="검증된 운영 노하우"
              desc="다년간 직영 운영으로 검증된 표준 매뉴얼 제공"
            />
          </div>
        </div>
      </section>

      {/* 가맹 안내 */}
      <section id="franchise" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">가맹점주 모집</h2>
          <p className="text-center text-gray-600 mb-12">함께 성장할 가맹점주를 모집합니다</p>
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">가맹 문의 절차</h3>
            <ol className="space-y-4 text-gray-700">
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <div>
                  <strong>전화 또는 이메일 상담 신청</strong>
                  <p className="text-sm text-gray-600 mt-1">아래 연락처로 가맹 의향과 희망 지역을 알려주세요</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <div>
                  <strong>본부 미팅</strong>
                  <p className="text-sm text-gray-600 mt-1">사업계획 / 가맹 조건 / 입지 분석 상담</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <div>
                  <strong>계약 체결 및 시스템 셋업</strong>
                  <p className="text-sm text-gray-600 mt-1">가맹 계약 후 NewAttend 시스템 구축 및 직원 교육</p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">4</span>
                <div>
                  <strong>오픈</strong>
                  <p className="text-sm text-gray-600 mt-1">본부 지원으로 안정적 오픈 및 지속 운영 컨설팅</p>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* 연락처 */}
      <section id="contact" className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">문의하기</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow border border-gray-200 p-8">
              <PhoneIcon className="h-10 w-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">전화 문의</h3>
              <p className="text-2xl font-bold text-blue-600">본부 지정 연락처</p>
              <p className="text-sm text-gray-500 mt-2">평일 09:00 ~ 18:00</p>
            </div>
            <div className="bg-white rounded-2xl shadow border border-gray-200 p-8">
              <EnvelopeIcon className="h-10 w-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">이메일 문의</h3>
              <a
                href="mailto:wlboy28@gmail.com"
                className="text-xl font-bold text-blue-600 hover:underline"
              >
                wlboy28@gmail.com
              </a>
              <p className="text-sm text-gray-500 mt-2">24시간 접수</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/logo-blue.png" alt="NewAttend" className="h-8 w-8 rounded" />
            <span className="text-xl font-bold text-white">NewAttend</span>
          </div>
          <p className="text-sm">AI 얼굴인식 출결 관리 프랜차이즈</p>
          <p className="text-xs mt-4">
            © {new Date().getFullYear()} NewAttend. All rights reserved.
            <span className="mx-2">|</span>
            <a href="https://ntd.newattend.com" className="hover:text-white">NTD 회사 정보</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; desc: string }> = ({ icon, title, desc }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
    <div className="text-blue-600 mb-4">{icon}</div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600">{desc}</p>
  </div>
);

export default NewAttendFranchiseHomePage;
